import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const TOKEN_KEY = "tta_token";

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  const t = auth.getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      auth.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default client;

// Convenience wrappers
export const api = {
  login: (pin) => client.post("/auth/login", { pin }).then((r) => r.data),
  verify: () => client.get("/auth/verify").then((r) => r.data),

  summary: () => client.get("/dashboard/summary").then((r) => r.data),

  devices: () => client.get("/devices").then((r) => r.data),
  createDevice: (d) => client.post("/devices", d).then((r) => r.data),
  updateDevice: (id, patch) => client.patch(`/devices/${id}`, patch).then((r) => r.data),
  deleteDevice: (id) => client.delete(`/devices/${id}`).then((r) => r.data),
  pingDevice: (id) => client.post(`/devices/${id}/ping`).then((r) => r.data),

  personas: () => client.get("/personas").then((r) => r.data),
  createPersona: (p) => client.post("/personas", p).then((r) => r.data),
  updatePersona: (id, patch) => client.patch(`/personas/${id}`, patch).then((r) => r.data),
  deletePersona: (id) => client.delete(`/personas/${id}`).then((r) => r.data),
  generatePersona: (brief) => client.post("/personas/generate", brief).then((r) => r.data),

  accounts: () => client.get("/accounts").then((r) => r.data),
  createAccount: (a) => client.post("/accounts", a).then((r) => r.data),
  updateAccount: (id, patch) => client.patch(`/accounts/${id}`, patch).then((r) => r.data),
  deleteAccount: (id) => client.delete(`/accounts/${id}`).then((r) => r.data),

  proxies: () => client.get("/proxies").then((r) => r.data),
  createProxy: (p) => client.post("/proxies", p).then((r) => r.data),
  deleteProxy: (id) => client.delete(`/proxies/${id}`).then((r) => r.data),

  videos: () => client.get("/videos").then((r) => r.data),
  createVideo: (v) => client.post("/videos", v).then((r) => r.data),
  updateVideo: (id, patch) => client.patch(`/videos/${id}`, patch).then((r) => r.data),
  deleteVideo: (id) => client.delete(`/videos/${id}`).then((r) => r.data),
  aiCaption: (req) => client.post("/videos/ai-caption", req).then((r) => r.data),
  aiVideoIdeas: (req) => client.post("/videos/ai-ideas", req).then((r) => r.data),
  aiRefreshPersona: (id) => client.post(`/personas/${id}/ai-refresh`).then((r) => r.data),
  aiAccountAdvice: (id) => client.post(`/accounts/${id}/ai-advice`).then((r) => r.data),
  uploadVideo: (file, opts = {}, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.assigned_account_id) fd.append("assigned_account_id", opts.assigned_account_id);
    if (opts.caption) fd.append("caption", opts.caption);
    return client
      .post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      })
      .then((r) => r.data);
  },
  videoFileUrl: (vid) => `${API}/videos/${vid}/file?token=${encodeURIComponent(auth.getToken() || "")}`,

  aiComments: (req) => client.post("/comments/ai", req).then((r) => r.data),

  schedule: () => client.get("/schedule").then((r) => r.data),
  createSchedule: (s) => client.post("/schedule", s).then((r) => r.data),
  deleteSchedule: (id) => client.delete(`/schedule/${id}`).then((r) => r.data),

  streams: () => client.get("/streams").then((r) => r.data),
  createStream: (s) => client.post("/streams", s).then((r) => r.data),
  deleteStream: (id) => client.delete(`/streams/${id}`).then((r) => r.data),

  // Warm-up
  warmupOverview: () => client.get("/warmup/overview").then((r) => r.data),
  warmupPlan: (accountId) => client.get(`/warmup/plan/${accountId}`).then((r) => r.data),
  createWarmupPlan: (accountId, regen = false) =>
    client.post(`/warmup/plan/${accountId}${regen ? "?regen=true" : ""}`).then((r) => r.data),
  warmupTick: (minutes = 30) =>
    client.post(`/warmup/tick?minutes=${minutes}`).then((r) => r.data),
  warmupAdvanceDay: (accountId) =>
    client.post(`/warmup/advance-day?account_id=${accountId}`).then((r) => r.data),

  bestTimes: (accountId, days = 3) =>
    client.get(`/best-times/${accountId}?days=${days}`).then((r) => r.data),

  // Comment Blast
  createCommentBlast: (payload) => client.post("/comment-blast", payload).then((r) => r.data),
  listCommentBlasts: () => client.get("/comment-blast").then((r) => r.data),
  runCommentBlast: () => client.post("/comment-blast/run").then((r) => r.data),
  deleteCommentBlast: (id) => client.delete(`/comment-blast/${id}`).then((r) => r.data),

  logs: (params = {}) => client.get("/logs", { params }).then((r) => r.data),

  alerts: () => client.get("/alerts").then((r) => r.data),
  ackAlert: (id) => client.patch(`/alerts/${id}/ack`).then((r) => r.data),
  deleteAlert: (id) => client.delete(`/alerts/${id}`).then((r) => r.data),

  analytics: () => client.get("/analytics/overview").then((r) => r.data),

  settings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (patch) => client.put("/settings", patch).then((r) => r.data),

  aiPing: () => client.get("/ai/ping").then((r) => r.data),
  seed: (force = false) => client.post(`/seed${force ? "?force=true" : ""}`).then((r) => r.data),

  bulkAccounts: (payload) => client.post("/accounts/bulk", payload).then((r) => r.data),

  // Agents
  listAgents: () => client.get("/agent/list").then((r) => r.data),

  // extras — proxies patch/check
  updateProxy: (id, patch) => client.patch(`/proxies/${id}`, patch).then((r) => r.data),
  checkProxy: (id) => client.post(`/proxies/${id}/check`).then((r) => r.data),

  // schedule extras
  updateSchedule: (id, patch) => client.patch(`/schedule/${id}`, patch).then((r) => r.data),
  markScheduleDone: (id) => client.post(`/schedule/${id}/mark-done`).then((r) => r.data),

  // streams extras
  updateStream: (id, patch) => client.patch(`/streams/${id}`, patch).then((r) => r.data),
  startStream: (id) => client.post(`/streams/${id}/start`).then((r) => r.data),
  stopStream: (id) => client.post(`/streams/${id}/stop`).then((r) => r.data),

  // alerts extras
  createAlert: (payload) => client.post("/alerts", payload).then((r) => r.data),
  resolveCaptcha: (id) => client.post(`/alerts/${id}/resolve-captcha`).then((r) => r.data),

  // accounts extras
  reanimateAccount: (id) => client.post(`/accounts/${id}/reanimate`).then((r) => r.data),
  accountTimeline: (id, limit = 40) =>
    client.get(`/accounts/${id}/timeline?limit=${limit}`).then((r) => r.data),

  // warmup single-action controls
  skipWarmupAction: (id) => client.post(`/warmup/actions/${id}/skip`).then((r) => r.data),
  completeWarmupAction: (id) => client.post(`/warmup/actions/${id}/complete`).then((r) => r.data),
  deleteWarmupAction: (id) => client.delete(`/warmup/actions/${id}`).then((r) => r.data),
};
